import { api } from "@/api/client";
import type { PartiaOut, RefName, ResolvedScan, ResolveRequest } from "@/api/order_check";
import type { TransferResolution } from "@/api/transfer_check";

// Скан-типы общие с проверкой перемещений: позиция устроена так же
// (`stock_transfer/schemas.py` реэкспортирует схемы `transfer_check`).
export type { PartiaOut, RefName, ResolvedScan, ResolveRequest } from "@/api/order_check";
/** Как разобрана приёмка — то же, что у проверки перемещений (один движок). */
export type { TransferResolution } from "@/api/transfer_check";

/** open — черновик (или документ создан, но не проведён: тогда есть `doc_ref`);
 *  sent — отправлено на приёмку (позиции заморожены, в 1С ничего);
 *  discrepancy — получатель принял не то, что отправлено: разбор;
 *  completed — документ создан (без приёмки — и проведён; через приёмку
 *  проведение доводит приёмка/разбор, см. `doc_posted_at`);
 *  cancelled — отменено разбором, в 1С ничего не осталось. */
export type StockTransferStatus = "open" | "sent" | "discrepancy" | "completed" | "cancelled";

export interface StockTransferHeader {
  id: string;
  display_number: number | null;
  organization_ref: string;
  organization_name: string;
  from_wh_ref: string;
  from_wh_name: string;
  to_wh_ref: string;
  to_wh_name: string;
  status: StockTransferStatus;
  doc_ref: string | null;
  doc_number: string | null;
  doc_created_at: string | null;
  doc_posted_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  items_count: number;
  qty_base_total: number;
  /** Через приёмку (склад-получатель или отправитель в списке «Приёмки
   *  перемещений»): вместо «Сохранить в 1С» — «Отправить на приёмку»,
   *  документ создаёт приёмка получателя. */
  requires_receipt: boolean;
  /** ISO; когда и кто (логин) отправил на приёмку. */
  sent_at: string | null;
  sent_by_name: string | null;
  /** ISO; когда получатель нажал «Принять». */
  received_at: string | null;
  resolution: TransferResolution | null;
  /** Решение сохранено, а шаги 1С не доведены — доделает приёмка/разбор. */
  pending_resolution: boolean;
}

export interface StockTransferItem {
  id: string;
  scan_kind: string;
  nomenclature_ref: string | null;
  nomenclature_name: string | null;
  nomenclature_article: string | null;
  characteristic_ref: string | null;
  characteristic_name: string | null;
  partia_ref: string | null;
  partia_name: string | null;
  partia_source: string | null;
  /** Сколько ЕДИНИЦ скана (коробов/штук) — то, что правит человек. */
  qty_scanned: number;
  /** В базовых единицах номенклатуры: `qty_scanned × коэффициент`. */
  qty_base: number;
  /** Имя базовой единицы номенклатуры для подписи количеств; пусто/нет —
   *  число без слова (единицу наугад не подставляем). */
  base_unit_name?: string;
  created_at: string;
  /** Код штуки ЧЗ в нормальной форме; не штука — пусто (`lib/scan-ui/marks.ts`). */
  mark?: string;
}

export interface StockTransferDetail {
  header: StockTransferHeader;
  items: StockTransferItem[];
  /** Спрашивать ли партию — профиль инсталляции (у Чекаловой партий нет). */
  partia_enabled: boolean;
}

export interface StockTransferList {
  open: StockTransferHeader[];
  /** Отправленные на приёмку, на разборе и принятые с непроведённым документом. */
  on_receipt: StockTransferHeader[];
  /** Проведённые и отменённые — последние 30. */
  recent: StockTransferHeader[];
}

export interface StockTransferItemCreate {
  scan_kind: string;
  raw?: string;
  ean13?: string | null;
  gtin?: string | null;
  serial?: string | null;
  nomenclature_ref: string;
  nomenclature_name?: string;
  nomenclature_article?: string;
  characteristic_ref?: string;
  characteristic_name?: string;
  partia_ref?: string;
  partia_name?: string;
  partia_source?: string;
  unit_ref?: string;
  unit_coefficient?: number;
  qty_scanned?: number;
  qty_base?: number;
}

export interface StockTransferConfig {
  scan_method: "tsd" | "camera" | "both";
}

/** Способ сканирования — из конфига РАЗДЕЛА: чужая ручка (сборки, приёмки)
 *  ответила бы 403 человеку без того права, и камера пропала бы молча. */
export async function getStockTransferConfig(): Promise<StockTransferConfig> {
  const { data } = await api.get<StockTransferConfig>("/stock-transfer/config");
  return data;
}

export async function listStockTransferOrganizations(q?: string): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/stock-transfer/organizations", {
    params: { q: q || undefined },
  });
  return data;
}

export async function listStockTransferWarehouses(q?: string): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/stock-transfer/warehouses", {
    params: { q: q || undefined },
  });
  return data;
}

export async function listStockTransfers(): Promise<StockTransferList> {
  const { data } = await api.get<StockTransferList>("/stock-transfer/sessions");
  return data;
}

export async function createStockTransfer(body: {
  organization_ref: string;
  from_wh_ref: string;
  to_wh_ref: string;
}): Promise<StockTransferDetail> {
  const { data } = await api.post<StockTransferDetail>("/stock-transfer/sessions", body);
  return data;
}

export async function getStockTransfer(id: string): Promise<StockTransferDetail> {
  const { data } = await api.get<StockTransferDetail>(`/stock-transfer/sessions/${id}`);
  return data;
}

export async function deleteStockTransfer(id: string): Promise<void> {
  await api.delete(`/stock-transfer/sessions/${id}`);
}

export async function resolveStockTransferScan(
  id: string,
  body: ResolveRequest,
): Promise<ResolvedScan> {
  const { data } = await api.post<ResolvedScan>(`/stock-transfer/sessions/${id}/resolve`, body);
  return data;
}

export async function lookupStockTransferPartia(
  id: string,
  nomenclatureRef: string,
  name: string,
): Promise<PartiaOut> {
  const { data } = await api.get<PartiaOut>(`/stock-transfer/sessions/${id}/partia`, {
    params: { nomenclature_ref: nomenclatureRef, name },
  });
  return data;
}

export async function addStockTransferItem(
  id: string,
  body: StockTransferItemCreate,
): Promise<StockTransferItem> {
  const { data } = await api.post<StockTransferItem>(`/stock-transfer/sessions/${id}/items`, body);
  return data;
}

/** Правятся ЕДИНИЦЫ скана, `qty_base` считает сервер (гейт — на добавку). */
export async function setStockTransferItemQty(
  id: string,
  itemId: string,
  units: number,
  unitCoefficient: number,
): Promise<StockTransferItem> {
  const { data } = await api.patch<StockTransferItem>(
    `/stock-transfer/sessions/${id}/items/${itemId}/quantity`,
    { units, unit_coefficient: unitCoefficient },
  );
  return data;
}

export async function deleteStockTransferItem(id: string, itemId: string): Promise<void> {
  await api.delete(`/stock-transfer/sessions/${id}/items/${itemId}`);
}

/** Создать (если ещё нет) и провести «Перемещение запасов». Повтор после
 *  сбоя проведения ДОДЕЛЫВАЕТ: документ уже создан, бэкенд только проводит. */
export async function saveStockTransfer(id: string): Promise<StockTransferDetail> {
  const { data } = await api.post<StockTransferDetail>(`/stock-transfer/sessions/${id}/save`);
  return data;
}

/** «Отправить на приёмку»: позиции замораживаются, в 1С НИЧЕГО не пишется —
 *  документ создаст «Принять» получателя. Отказ (гейт остатка, уже отправлено,
 *  не через приёмку, пусто) — 409 с текстом. */
export async function sendStockTransfer(id: string): Promise<StockTransferDetail> {
  const { data } = await api.post<StockTransferDetail>(`/stock-transfer/sessions/${id}/send`);
  return data;
}
